import { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { SongbookUb } from "../gql/graphql";
import { useUpsertSongbookMutation } from "./useUpsertSongbookMutation";
import { v4 as uuidv4 } from "uuid";
import { useHotkeys } from "react-hotkeys-hook";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  bucket: string;
  songbook?: SongbookUb;
  onSaved?: () => void;
}

export function SongbookDialog({
  isOpen,
  toggle,
  bucket,
  songbook,
  onSaved,
}: Props) {
  const [name, setName] = useState("");
  const [mnemonic, setMnemonic] = useState("");
  const [, upsertSongbook] = useUpsertSongbookMutation();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const mnemonicRegex = /^[A-Z][A-Z1-9]*$/;
  const isMnemonicValid = mnemonicRegex.test(mnemonic);
  const isValid = name.trim().length > 0 && isMnemonicValid;

  useEffect(() => {
    if (songbook) {
      setName(songbook.name);
      setMnemonic(songbook.mnemonic);
    } else {
      setName("");
      setMnemonic("");
    }
  }, [songbook, isOpen]);

  const handleSave = () => {
    if (!isValid) return;
    const id = songbook?.id ?? uuidv4();
    upsertSongbook({ id, name, mnemonic, bucket }).then((result) => {
      if (!result.error) {
        onSaved?.();
        toggle();
      }
    });
  };

  const handleOpened = () => {
    nameInputRef.current?.focus();
  };

  useHotkeys(
    "enter",
    (e) => {
      e.preventDefault();
      if (isValid) {
        handleSave();
      }
    },
    {
      enableOnFormTags: ["input"],
      enabled: isOpen,
    },
  );

  useHotkeys(
    "escape",
    (e) => {
      e.preventDefault();
      toggle();
    },
    {
      enableOnFormTags: ["input"],
      enabled: isOpen,
    },
  );

  const handleMnemonicChange = (val: string) => {
    const upper = val.toUpperCase();
    const filtered = upper.replace(/[^A-Z1-9]/g, "").slice(0, 6);
    setMnemonic(filtered);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} onOpened={handleOpened}>
      <ModalHeader toggle={toggle}>
        {t(
          songbook
            ? "songbooks.dialog.editTitle"
            : "songbooks.dialog.createTitle",
        )}
      </ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label for="songbookName">{t("songbooks.dialog.nameLabel")}</Label>
            <Input
              innerRef={nameInputRef}
              type="text"
              id="songbookName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("songbooks.dialog.namePlaceholder")}
            />
          </FormGroup>
          <FormGroup>
            <Label for="songbookMnemonic">
              {t("songbooks.dialog.mnemonicLabel")}
            </Label>
            <Input
              type="text"
              id="songbookMnemonic"
              value={mnemonic}
              onChange={(e) => handleMnemonicChange(e.target.value)}
              placeholder={t("songbooks.dialog.mnemonicPlaceholder")}
              invalid={mnemonic.length > 0 && !isMnemonicValid}
              maxLength={6}
            />
            {mnemonic.length > 0 && !mnemonicRegex.test(mnemonic[0]) && (
              <div className="invalid-feedback d-block">
                {t("songbooks.dialog.mnemonicMustStartWithLetter")}
              </div>
            )}
            <small className="form-text text-muted">
              {t("songbooks.dialog.mnemonicHelp")}
            </small>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {t("common.buttons.cancel")}
        </Button>
        <Button color="primary" onClick={handleSave} disabled={!isValid}>
          {t("common.buttons.save")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
